import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import Dropdown from '../../../inputs/Dropdown';
import InputTypes from '../../../inputs/InputTypes';
import AddOptionButton from '../../../inputs/AddOptionButton';
import DisplayOptions from './DisplayOptions';
import {
  addVehicleColumnOption,
  updateVehicleColumn,
} from '../../../actions/apiDepartmentUpdates';
import { setCurrentTab } from '../../../actions/apiSetInfo';
import DropdownMultiple from '../../../inputs/DropdownMultiple';
import IconPickerPopupVehicleColumn from './IconPickerPopupVehicleColumn';

const TabEditVehicleColumn = ({ vehicle_column_id }) => {
  const dispatch = useDispatch();

  const [option_name, setOptionName] = useState('');
  const [automove_trigger_value, setAutomoveTriggerValue] = useState('');
  const [localVehicleColumn, setLocalVehicleColumn] = useState(null);

  const vehicle_columns = useSelector((state) => state.settings_departments.vehicle_columns);
  const departments = useSelector((state) => state.settings_departments.original_departments);
  const input_types = useSelector((state) => state.settings_departments.input_types);
  const all_filters = useSelector((state) => state.settings_site.all_filters);
  const set_dealerships = useSelector((state) => state.settings_dealerships.related_installations);

  useEffect(() => {
    const value = _.find(vehicle_columns, { id: vehicle_column_id });
    setLocalVehicleColumn(value || {});
  }, [vehicle_columns, vehicle_column_id]);

  useEffect(() => {
    if (localVehicleColumn) {
      setAutomoveTriggerValue(localVehicleColumn.automove_trigger_value || '');
    }
  }, [localVehicleColumn]);

  const onChange = useCallback((e) => {
    setOptionName(e.target.value);
  }, []);

  const onClick = useCallback((e) => {
    e.preventDefault();
    const id = localVehicleColumn.id;
    const errorType = 'addVehicleColumnOption';
    const data = {
      vehicle_column_id: id,
      name: option_name,
      special: '',
      active: 'true',
    };
    dispatch(addVehicleColumnOption(id, data, errorType));
    setOptionName('');
  }, [dispatch, localVehicleColumn, option_name]);

  const onTabClick = useCallback((e) => {
    dispatch(setCurrentTab(e.target.name));
  }, [dispatch]);

  const onBlur = useCallback((e) => {
    const name = e.target.name;
    const id = localVehicleColumn.id;
    let value = e.target.value;
    const errorType = 'editVehicleColumnError';
    if (name === 'name') {
      value = value.toLowerCase();
    }
    const data = {
      id,
      [name]: value,
    };
    dispatch(updateVehicleColumn(id, data, errorType));
  }, [dispatch, localVehicleColumn]);

  const inputColumns = useMemo(() => {
    return input_types.map((option) => ({
      id: option.id,
      label: option.name,
      value: option.name,
      selected: localVehicleColumn?.input_name === parseInt(option.id),
    }));
  }, [input_types, localVehicleColumn]);

  const automoveDestinations = useMemo(() => {
    const remove_department_types = ['recently sold', 'data step', 'all reconditioning'];
    return departments
      .filter((option) => !remove_department_types.includes(option.department_type))
      .map((option) => ({
        id: option.id,
        label: option.name,
        value: option.id,
        selected: false,
      }));
  }, [departments]);

  const selectRelatedInstallations = useMemo(() => {
    const selected_dealerships = localVehicleColumn?.location ? localVehicleColumn.location.split(',') : [];
    return set_dealerships.map((option) => ({
      id: option.id,
      label: option.subdomain,
      name: option.subdomain,
      selected: selected_dealerships.includes(option.id.toString()),
    }));
  }, [set_dealerships, localVehicleColumn]);

  const selectDepartments = useMemo(() => {
    return departments.map((option) => ({
      value: option.id,
      label: option.name || option.name,
    }));
  }, [departments]);

  const allFilters = useMemo(() => {
    return all_filters.map((option) => ({
      value: option.id,
      label: `${option.filter_name} (${option.filter_target} ${option.filter_operator} ${option.filter_value})`,
    }));
  }, [all_filters]);

  const renderContent = () => {
    const cols = [];
    const destinations = [];

    if (!localVehicleColumn?.name) return null;

    cols.push(
      <div className="card" key="name-column">
        <div className="d-flex flex-column pad-left-2 pad-right-2">
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Name</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            The column name should not have things like "@-/.?*^?" or "&" and should be case insensitive for best practices. However, the display name has no limitations.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              value={localVehicleColumn.name}
              type="text"
              name="name"
              updatetype="vehicle_column"
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Choose Display Name -- If different then vehicle column name capitalized</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Display name is handy when using special characters in the column name. The column name should not have things like "@-/.?*^?" or "&" for best practices. However, the display name has no limitations.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="text"
              value={localVehicleColumn.display_name}
              name="display_name"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Label 1 of Switch Display Name - Default is "Needed"</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="text"
              value={localVehicleColumn.display_label_1}
              name="display_label_1"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Label 2 of Switch Display Name - Default is "Completed"</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="text"
              value={localVehicleColumn.display_label_2}
              name="display_label_2"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Set Initial Default Value added during the feed</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            ----------- Sets the default value for all vehicles the next feed (Careful - there is not an undo) ------------------------
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.has_default_value}
              name="has_default_value"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
          <div className="p-2 text-dark">
            <InputTypes
              type="text"
              value={localVehicleColumn.default_value}
              name="default_value"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Set Default Value"
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Input Type</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Input types are special components and describe the initial functionality of a vehicle column. See <i>Input Type Definitions</i> for further explanation of types.
          </p>
          <div className="p-2 text-dark">
            <Dropdown
              titleHelper=""
              title="Choose Input Type"
              value={localVehicleColumn.input_name}
              name="input_name"
              vehicle_column_id={localVehicleColumn.id}
              update_type="vehicle_column"
              className="form-control"
              list={inputColumns}
            />
          </div>
        </div>
      </div>
    );

    cols.push(
      <div className="card">
        <div className="card-header bg-dark text-white" />
        <div className="p-2 text-dark">
          <label className="control-label bg-dark text-white">Options</label>
        </div>
        <p className="text-gray">
          <i className="fa fa-info-circle text-gray" />
          Add/Delete Options.
        </p>
        <div className="d-flex flex-column pad-left-2 pad-right-2">
          <div className="p-2 text-dark">
            <AddOptionButton
              field="option_name"
              label=""
              onChange={onChange}
              onClick={onClick}
              value={option_name}
              type="add_option"
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
          <DisplayOptions
            options={localVehicleColumn.options}
            column_id={localVehicleColumn.id}
            key={`display_options_${localVehicleColumn.id}`}
          />
        </div>
      </div>
    );

    cols.push(
      <div className="card">
        <div className="card-header bg-dark text-white" />
        <div className="d-flex flex-column pad-left-2 pad-right-2">
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">AutoNote On/Off</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Turn on an automatic note to be created based on a value of this vehicle column.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.autonote}
              name="autonote"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onBlur={onBlur}
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">AutoNote Template (default note is column name)</label>
            <p className="text-gray">
              <i className="fa fa-info-circle text-gray" />
              Use the format of "^name^ was marked ^value^". ^name^ will be replaced by the vehicle column name and ^value^ will be replaced by the value of the column.
            </p>
            <InputTypes
              type="text"
              value={localVehicleColumn.autonote_text}
              name="autonote_text"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
        </div>
      </div>
    );

    cols.push(
      <div className="card">
        <div className="card-header bg-dark text-white" />
        <div className="d-flex flex-column pad-left-2 pad-right-2">
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Notification On/Off</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Currently not available - will allow you to send a notification based on a value of this column.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.notification}
              name="notification"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onBlur={onBlur}
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Notification Trigger Value</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Currently not available - the value the column must be to trigger a notification.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="text"
              value={localVehicleColumn.notification_trigger_value}
              name="notification_trigger_value"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Notification Text -- Include a message for this column</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Currently not available - The text to be sent in the notification. Use the format of "^name^ was marked ^value^". ^name^ will be replaced by the vehicle column name and ^value^ will be replaced by the value of the column.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="text"
              value={localVehicleColumn.notification_text}
              name="notification_text"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Notification Users -- Send Notification to Users</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Currently not available - the selected users will receive a notification based on the trigger value of the column.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="text"
              value={localVehicleColumn.notification_users}
              name="notification_users"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
        </div>
      </div>
    );

    cols.push(
      <div className="card">
        <div className="card-header bg-dark text-white" />
        <div className="d-flex flex-column pad-left-2 pad-right-2">
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Location Only - On/Off</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Multisite Store Feature -- column will only show up in a specific location. Most often used for "Techs" so you can reuse the Techs column but make each dropdown list store specific. The actual vehicle column name would be "xyz store - Techs" and "abc store - Techs" and then change display name to "Techs".
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.location_only}
              name="location_only"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onBlur={onBlur}
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Locations -- (dealership)</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Multisite Store Feature -- choose the locations/dealerships this column should display.
          </p>
          <div className="p-2 text-dark">
            <DropdownMultiple
              titleHelper="Location"
              title="Location"
              list={selectRelatedInstallations}
              update_type="vehicle_column_locations"
              vehicle_column_id={localVehicleColumn.id}
              dealers={localVehicleColumn.location}
            />
          </div>
        </div>
      </div>
    );

    cols.push(
      <div className="card">
        <div className="card-header bg-dark text-white" />
        <div className="d-flex flex-column pad-left-2 pad-right-2">
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">HelpNote On/Off</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Add a help message to be included in a hover or popup event next to the column.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.helpnote}
              name="helpnote"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onBlur={onBlur}
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">HelpNote Text -- Include a help message for this column</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            The message you would like to display to a user who hovers or clicks the help icon next to the column.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="text"
              value={localVehicleColumn.helpnote_text}
              name="helpnote_text"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
        </div>
      </div>
    );

    cols.push(
      <div className="card">
        <div className="card-header bg-dark text-white" />
        <div className="d-flex flex-column pad-left-2 pad-right-2">
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">AutoMove On/Off</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Create an automated move to another department/stage/bucket/queue based on a value of this column.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.automove}
              name="automove"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onBlur={onBlur}
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">AutoMove Trigger Value</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            The trigger value to initiate the move. Usually checkboxes would be a value of "true" or the switch value would be either "needed" or "completed". This value is also case insensitive.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="text"
              value={localVehicleColumn.automove_trigger_value}
              name="automove_trigger_value"
              updatetype="vehicle_column"
              setdisabled={false}
              vehicle_column_id={localVehicleColumn.id}
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Department Only On/Off</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Turn this on to allow this automove only to occur if the vehicle is in a current department/stage/bucket/queue (not data steps, only locations) - you must use the "stocktype destinations" below.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.department_only}
              name="department_only"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onBlur={onBlur}
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">StockType Destinations On/Off must be used if you are using the above Department Only On/Off</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Turn this on if the automove destination is different based on the stocktype of "New","Used", or "Wholesale".
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.stocktype_dependent}
              name="stocktype_dependent"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onBlur={onBlur}
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Filters On/Off</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Currently not available - Add filters/rules that must pass in order for the action to happen.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.filter_dependent}
              name="filter_dependent"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onBlur={onBlur}
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
          <div className="p-2 text-dark">
            <label className="control-label bg-dark text-white">Condition Dependent On/Off</label>
          </div>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Currently not available - Turn this on to create conditional automove destinations.
          </p>
          <div className="p-2 text-dark">
            <InputTypes
              type="checkbox-edit"
              value={localVehicleColumn.condition_dependent}
              name="condition_dependent"
              updatetype="vehicle_column"
              setdisabled={false}
              rowid={localVehicleColumn.id}
              onBlur={onBlur}
              vehicle_column_id={localVehicleColumn.id}
            />
          </div>
        </div>
      </div>
    );

    // Render default destination if automove is enabled
    if (localVehicleColumn.automove === true) {
      destinations.push(
        <div className="p-2 text-dark" key={`default_destination-${localVehicleColumn.id}`}>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Default Destination.
          </p>
          <div className="form-group">
            <label className="control-label bg-dark text-white">AutoMove Destination</label>
            <Dropdown
              titleHelper=""
              title="Choose AutoMove Destination"
              value={localVehicleColumn.automove_destination}
              name="automove_destination"
              vehicle_column_id={localVehicleColumn.id}
              update_type="vehicle_column"
              className="form-control"
              list={automoveDestinations}
            />
          </div>
        </div>
      );
    }

    // Render stocktype-dependent destinations if stocktype_dependent is enabled
    if (localVehicleColumn.stocktype_dependent === true) {
      destinations.push(
        <div className="p-2 text-dark" key={`stocktype_dependent_new-${localVehicleColumn.id}`}>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Destination for "New" stocktypes.
          </p>
          <div className="form-group">
            <label className="control-label bg-dark text-white">AutoMove Destination New</label>
            <Dropdown
              titleHelper=""
              title="Choose AutoMove Destination"
              value={localVehicleColumn.ad_new}
              name="ad_new"
              vehicle_column_id={localVehicleColumn.id}
              update_type="vehicle_column"
              className="form-control"
              list={automoveDestinations}
            />
          </div>
        </div>
      );
      destinations.push(
        <div className="p-2 text-dark" key={`stocktype_dependent_used-${localVehicleColumn.id}`}>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Destination for "Used" stocktypes.
          </p>
          <div className="form-group">
            <label className="control-label bg-dark text-white">AutoMove Destination Used</label>
            <Dropdown
              titleHelper=""
              title="Choose AutoMove Destination"
              value={localVehicleColumn.ad_used}
              name="ad_used"
              vehicle_column_id={localVehicleColumn.id}
              update_type="vehicle_column"
              className="form-control"
              list={automoveDestinations}
            />
          </div>
        </div>
      );
      destinations.push(
        <div className="p-2 text-dark" key={`stocktype_dependent_wholesale-${localVehicleColumn.id}`}>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Destination for "Wholesale" stocktypes.
          </p>
          <div className="form-group">
            <label className="control-label bg-dark text-white">AutoMove Destination Wholesale</label>
            <Dropdown
              titleHelper=""
              title="Choose AutoMove Destination"
              value={localVehicleColumn.ad_wholesale}
              name="ad_wholesale"
              vehicle_column_id={localVehicleColumn.id}
              update_type="vehicle_column"
              className="form-control"
              list={automoveDestinations}
            />
          </div>
        </div>
      );
    }

    // Render condition-dependent destinations if condition_dependent is enabled
    if (localVehicleColumn.condition_dependent === true) {
      destinations.push(
        <div className="p-2 text-dark" key={`condition_dependent_1-${localVehicleColumn.id}`}>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Destination for "Condition scenario 1".
          </p>
          <div className="form-group">
            <label className="control-label bg-dark text-white">AutoMove Destination Condition 1</label>
            <Dropdown
              titleHelper=""
              title="Choose AutoMove Destination"
              value={localVehicleColumn.ad_condition_1}
              name="ad_condition_1"
              vehicle_column_id={localVehicleColumn.id}
              update_type="vehicle_column"
              className="form-control"
              list={automoveDestinations}
            />
          </div>
        </div>
      );
      destinations.push(
        <div className="p-2 text-dark" key={`condition_dependent_2-${localVehicleColumn.id}`}>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Destination for "Condition scenario 2".
          </p>
          <div className="form-group">
            <label className="control-label bg-dark text-white">AutoMove Destination Condition 2</label>
            <Dropdown
              titleHelper=""
              title="Choose AutoMove Destination"
              value={localVehicleColumn.ad_condition_2}
              name="ad_condition_2"
              vehicle_column_id={localVehicleColumn.id}
              update_type="vehicle_column"
              className="form-control"
              list={automoveDestinations}
            />
          </div>
        </div>
      );
      destinations.push(
        <div className="p-2 text-dark" key={`condition_dependent_3-${localVehicleColumn.id}`}>
          <p className="text-gray">
            <i className="fa fa-info-circle text-gray" />
            Destination for "Condition scenario 3".
          </p>
          <div className="form-group">
            <label className="control-label bg-dark text-white">AutoMove Destination Condition 3</label>
            <Dropdown
              titleHelper=""
              title="Choose AutoMove Destination"
              value={localVehicleColumn.ad_condition_3}
              name="ad_condition_3"
              vehicle_column_id={localVehicleColumn.id}
              update_type="vehicle_column"
              className="form-control"
              list={automoveDestinations}
            />
          </div>
        </div>
      );
    }

    // Render required current departments if department_only is enabled
    if (localVehicleColumn.department_only === true) {
      if (localVehicleColumn.stocktype_dependent === true) {
        cols.push(
          <div className="p-2 text-dark" key={`department_new-${localVehicleColumn.id}`}>
            <p className="text-gray">
              <i className="fa fa-info-circle text-gray" />
              The Required Department/Stage/Queue/Bucket the New vehicle must be in to automove.
            </p>
            <div className="form-group">
              <label className="control-label bg-dark text-white">Required Current Department -- New</label>
              <Dropdown
                titleHelper=""
                title="Choose Required Current Department"
                value={localVehicleColumn.current_department_new}
                name="current_department_new"
                vehicle_column_id={localVehicleColumn.id}
                update_type="vehicle_column"
                className="form-control"
                list={automoveDestinations}
              />
            </div>
          </div>
        );
        cols.push(
          <div className="p-2 text-dark" key={`department_used-${localVehicleColumn.id}`}>
            <p className="text-gray">
              <i className="fa fa-info-circle text-gray" />
              The Required Department/Stage/Queue/Bucket the Used vehicle must be in to automove.
            </p>
            <div className="form-group">
              <label className="control-label bg-dark text-white">Required Current Department -- Used</label>
              <Dropdown
                titleHelper=""
                title="Choose Required Current Department"
                value={localVehicleColumn.current_department_used}
                name="current_department_used"
                vehicle_column_id={localVehicleColumn.id}
                update_type="vehicle_column"
                className="form-control"
                list={automoveDestinations}
              />
            </div>
          </div>
        );
        cols.push(
          <div className="p-2 text-dark" key={`department_wholesale-${localVehicleColumn.id}`}>
            <p className="text-gray">
              <i className="fa fa-info-circle text-gray" />
              The Required Department/Stage/Queue/Bucket the Wholesale vehicle must be in to automove.
            </p>
            <div className="form-group">
              <label className="control-label bg-dark text-white">Required Current Department -- Wholesale</label>
              <Dropdown
                titleHelper=""
                title="Choose Required Current Department"
                value={localVehicleColumn.current_department_wholesale}
                name="current_department_wholesale"
                vehicle_column_id={localVehicleColumn.id}
                update_type="vehicle_column"
                className="form-control"
                list={automoveDestinations}
              />
            </div>
          </div>
        );
      } else {
        cols.push(
          <div className="p-2 text-dark" key={`department_default-${localVehicleColumn.id}`}>
            <p className="text-gray">
              <i className="fa fa-info-circle text-gray" />
              The Required Department/Stage/Queue/Bucket the vehicle must be in to automove.
            </p>
            <div className="form-group">
              <label className="control-label bg-dark text-white">Required Current Department</label>
              <Dropdown
                titleHelper=""
                title="Choose Required Current Department"
                value={localVehicleColumn.current_department}
                name="current_department"
                vehicle_column_id={localVehicleColumn.id}
                update_type="vehicle_column"
                className="form-control"
                list={automoveDestinations}
              />
            </div>
          </div>
        );
      }
    }

    cols.push(
      <div className="card" key={`destinations-${localVehicleColumn.id}`}>
        <div className="d-flex flex-column pad-left-2 pad-right-2">
          {destinations}
        </div>
      </div>
    );

    return (
      <div className="container pad-bottom-2 edit-vehicle-column-page bg-dark" key={`container-${localVehicleColumn.id}`}>
        {cols}
      </div>
    );
  };

  return renderContent();
};

export default TabEditVehicleColumn;